import { useEffect, useMemo, useState } from 'preact/hooks'
import { emailBlastApi } from '@/api/emailBlast'
import type { BlastRecipientDto, BlastRecipientsPageDto } from '@/types/emailBlast'
import { routes } from '@/utils/routes'
import { lowerSafe } from '@/utils/strings'
import { DataGrid, type ColumnConfig } from '@/components/common/grids/DataGrid'
import { AnalyticsExportButton } from '@/components/dashboard/analytics/AnalyticsExportButton'
import { getRecipientComparator, reachedRecipient } from './blastRecipientSort'

const PAGE_SIZE = 10

interface Props {
  workspaceId: string
  clientCompanyId: string
  campaignId: string
  webId: string
  blastId: string
}

/**
 * Recipient drill-down for the blast detail (ORB-384): a paged, searchable, sortable list of the blast's
 * recipients with their status, opens, clicks, and last click. Each recipient deep-links to its ORB-383
 * contact route (lead anchor preferred, else the audience FileImportRow anchor).
 *
 * ORB-706 loads the whole list in one read and lets the grid sort, page and filter it in the browser.
 * The earlier server-paged version could not offer sorting honestly: the query is ordered by recipient
 * address, so sorting a fetched page ranks the rows that happened to be loaded rather than the blast.
 * Holding the full set is what makes the sort controls mean what they appear to mean — and it is the
 * same volume the CSV export has always read.
 *
 * Export stays server-streamed. Its CSV carries fifteen columns, four of which (complaint time,
 * current unsubscribe state, unsubscribe time, domain block) are never projected to the browser, so the
 * grid's own exporter would quietly write a narrower file.
 */
export function BlastRecipientsSection({ workspaceId, clientCompanyId, campaignId, webId, blastId }: Props) {
  const [search, setSearch] = useState('')
  const [data, setData] = useState<BlastRecipientsPageDto | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const controller = new AbortController()
    setLoading(true)
    setError(null)
    emailBlastApi
      .recipients(workspaceId, blastId, { signal: controller.signal })
      .then(response => {
        if (controller.signal.aborted) return
        if (response.data) setData(response.data)
        else setError(response.error || 'Failed to load recipients')
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false)
      })
    return () => controller.abort()
  }, [workspaceId, blastId])

  const allRecipients = data?.Items ?? []

  // Filtering in the browser, over the whole list, so the count and the sort agree with each other.
  const recipients = useMemo(() => {
    const term = lowerSafe(search.trim())
    if (!term) return allRecipients
    return allRecipients.filter(
      recipient =>
        lowerSafe(recipient.RecipientEmail).includes(term) ||
        lowerSafe(recipient.RecipientName ?? '').includes(term),
    )
  }, [allRecipients, search])

  const contactHref = useMemo(
    () =>
      (recipient: BlastRecipientDto): string | null => {
        const scoped = routes.scoped(clientCompanyId, workspaceId)
        if (recipient.LeadId) return scoped.webContact(campaignId, webId, 'lead', recipient.LeadId)
        if (recipient.FileImportRowId)
          return scoped.webContact(campaignId, webId, 'audience', recipient.FileImportRowId)
        return null
      },
    [clientCompanyId, workspaceId, campaignId, webId],
  )

  const columns = useMemo<ColumnConfig<BlastRecipientDto>[]>(
    () => [
      {
        key: 'recipient',
        header: 'Recipient',
        sortable: true,
        render: recipient => {
          const href = contactHref(recipient)
          return (
            <div className="min-w-0">
              {href ? (
                <a href={href} className="font-medium text-primary-700 hover:underline dark:text-primary-400">
                  {recipient.RecipientName || recipient.RecipientEmail}
                </a>
              ) : (
                <span className="font-medium text-slate-800 dark:text-slate-200">
                  {recipient.RecipientName || recipient.RecipientEmail}
                </span>
              )}
              {recipient.RecipientName && (
                <span className="block text-xs text-slate-400 dark:text-slate-500">{recipient.RecipientEmail}</span>
              )}
            </div>
          )
        },
      },
      { key: 'status', header: 'Status', sortable: true, render: recipient => <StatusBadge status={recipient.Status} /> },
      {
        key: 'opens',
        header: 'Opens',
        sortable: true,
        align: 'right',
        render: recipient => engagementCell(recipient, recipient.OpenCount),
      },
      {
        key: 'clicks',
        header: 'Clicks',
        sortable: true,
        align: 'right',
        render: recipient => engagementCell(recipient, recipient.ClickCount),
      },
      {
        key: 'lastClick',
        header: 'Last click',
        hidden: 'hidden sm:table-cell',
        render: recipient => (
          <span
            className="block max-w-[16rem] truncate text-slate-500 dark:text-slate-400"
            title={recipient.LastClickedUrl || undefined}
          >
            {recipient.LastClickedUrl || '—'}
          </span>
        ),
      },
    ],
    [contactHref],
  )

  return (
    <section className="space-y-3">
      {/* The fetch error rides ABOVE a retained grid rather than through DataGrid's `error` prop, which
          replaces the whole table with a dead end that offers no way back (UserManagement rejected the
          same trade-off). A failed refresh should not erase the page you were reading. */}
      {error && <p className="text-sm text-red-600 dark:text-red-400">{error}</p>}

      <DataGrid<BlastRecipientDto>
        title="Recipients"
        data={recipients}
        columns={columns}
        getRowKey={recipient => recipient.Id}
        getSortComparator={getRecipientComparator}
        defaultSortKey="recipient"
        defaultSortDirection="asc"
        pagination
        pageSize={PAGE_SIZE}
        pageResetKey={search}
        isLoading={loading && allRecipients.length === 0}
        emptyMessage={search ? 'No recipients match your search.' : 'No recipients yet.'}
        search={{
          value: search,
          onChange: setSearch,
          placeholder: 'Search recipients',
          ariaLabel: 'Search recipients',
        }}
        toolbarActions={
          /* Exports the whole blast (ORB-450), independent of the search and sort above. */
          <AnalyticsExportButton
            onExport={() => emailBlastApi.exportRecipients(workspaceId, blastId)}
            fallbackFilename={`broadcast-recipients-${blastId}.csv`}
          />
        }
      />
    </section>
  )
}

/**
 * Opens/clicks show only once the row reached delivery; queued / scheduled / bounced (never-delivered)
 * rows read "—", not a misleading 0 (review: Copilot — !BouncedAtUtc alone treated undelivered as
 * reached). The sort applies the same rule — see blastRecipientSort.
 */
function engagementCell(recipient: BlastRecipientDto, count: number) {
  return (
    <span className="tabular-nums text-slate-600 dark:text-slate-300">
      {reachedRecipient(recipient) ? count.toLocaleString() : '—'}
    </span>
  )
}

function StatusBadge({ status }: { status: string }) {
  const tone = (() => {
    const normalized = lowerSafe(status)
    if (normalized.includes('click') || normalized.includes('open'))
      return 'bg-primary-50 text-primary-700 dark:bg-primary-950/40 dark:text-primary-400'
    if (normalized.includes('bounce') || normalized.includes('fail') || normalized.includes('complain'))
      return 'bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-400'
    if (normalized.includes('schedul') || normalized.includes('queue'))
      return 'bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-400'
    return 'bg-slate-100 text-slate-600 dark:bg-slate-800 dark:text-slate-300'
  })()
  return <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>{status}</span>
}
